import React, { useMemo, useRef, useState, useEffect, useLayoutEffect } from 'react'
import { createPortal } from 'react-dom'

const Operators = {
  numerical: [
    { k: 'equals', l: 'Equals' },
    { k: 'notEquals', l: 'Does not equal' },
    { k: 'gt', l: 'Greater than' },
    { k: 'gte', l: 'Greater than or equal to' },
    { k: 'lt', l: 'Less than' },
    { k: 'lte', l: 'Less than or equal to' },
    { k: 'between', l: 'Between' },
    { k: 'gt_col', l: 'Greater than Column' },
    { k: 'lt_col', l: 'Less than Column' },
    { k: 'eq_col', l: 'Equal to Column' },
    { k: 'blank', l: 'Blank' },
    { k: 'notBlank', l: 'Not blank' },
  ],
}

function usePortalPosition(anchorRef, width = 320) {
  const [coords, setCoords] = useState({ top: 0, left: 0, width })
  const update = () => {
    if (!anchorRef.current) return
    const r = anchorRef.current.getBoundingClientRect()
    let left = r.left
    if (left + width + 8 > window.innerWidth) left = window.innerWidth - width - 8
    if (left < 8) left = 8
    const top = Math.min(r.bottom + 8, window.innerHeight - 8)
    setCoords({ top, left, width })
  }
  return { coords, update }
}

const CategoricalMultiSelect = ({ anchorRef, options = [], value, onChange }) => {
  const menuRef = useRef(null)
  const { coords, update } = usePortalPosition(anchorRef, 360)
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')

  useLayoutEffect(() => {
    if (!open) return
    update()
    const onResize = () => update()
    window.addEventListener('resize', onResize)
    window.addEventListener('scroll', onResize, true)
    return () => {
      window.removeEventListener('resize', onResize)
      window.removeEventListener('scroll', onResize, true)
    }
  }, [open])

  useEffect(() => {
    const onDoc = (e) => {
      const insideMenu = menuRef.current && menuRef.current.contains(e.target)
      const insideButton = anchorRef.current && anchorRef.current.contains(e.target)
      if (!insideMenu && !insideButton) setOpen(false)
    }
    if (open) document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  const filtered = useMemo(() => {
    return (options || []).filter((o) => o.toString().toLowerCase().includes(query.toLowerCase()))
  }, [options, query])
  const selected = new Set(value || [])
  const toggle = (item) => {
    const next = new Set(selected)
    if (next.has(item)) next.delete(item); else next.add(item)
    onChange(Array.from(next))
  }

  return (
    <>
      <button ref={anchorRef} onClick={() => setOpen((o) => !o)} className="h-8 px-3 border rounded bg-white hover:bg-gray-50 text-sm">
        {selected.size ? `${selected.size} selected` : 'Any'}
      </button>
      {open && createPortal(
        <div ref={menuRef} className="z-50 bg-white border border-gray-200 rounded-lg shadow-xl p-3" style={{ position: 'fixed', top: coords.top, left: coords.left, width: coords.width, maxHeight: '70vh', overflow: 'auto' }}>
          <input className="w-full h-9 px-3 text-sm border border-gray-300 rounded mb-2" placeholder="Search..." value={query} onChange={(e) => setQuery(e.target.value)} />
          <div className="max-h-60 overflow-auto space-y-1 mt-1">
            <div className="flex items-center justify-between px-1 py-0.5 text-xs text-gray-500">
              <button className="hover:underline" onClick={() => onChange(filtered)}>Select All</button>
              <button className="hover:underline" onClick={() => onChange([])}>Deselect All</button>
            </div>
            {filtered.map((opt) => (
              <label key={opt} className="flex items-center gap-2 text-sm px-1 py-0.5">
                <input type="checkbox" className="accent-blue-600" checked={selected.has(opt)} onChange={() => toggle(opt)} />
                <span className="truncate" title={opt}>{opt}</span>
              </label>
            ))}
          </div>
        </div>,
        document.body
      )}
    </>
  )
}

const FilterChip = ({ fields, field, value, onChange, onRemove }) => {
  // Graceful fallback if field is not found (e.g. loading or mismatch key)
  const safeField = field || {
    key: value.key,
    label: value.key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
    type: 'numerical' // Default to numerical as it is safer for unknown fields (categorical needs options)
  }

  const { label, type, options } = safeField

  const anchorRef = useRef(null)
  if (type === 'categorical') {
    // Robustly handle value: can be array (op='in') or single string (op='eq')
    const rawVal = value?.value
    const safeVal = Array.isArray(rawVal) ? rawVal : (rawVal ? [rawVal] : [])

    return (
      <div className="flex items-center gap-2 border rounded px-2 py-1 bg-white">
        <span className="text-sm text-gray-700">{label}</span>
        <CategoricalMultiSelect
          anchorRef={anchorRef}
          options={options || []}
          value={safeVal}
          onChange={(vals) => onChange({ op: 'in', value: vals })}
        />
        <button onClick={onRemove} className="text-gray-400 hover:text-gray-600" title="Remove">×</button>
      </div>
    )
  }
  // numerical
  const op = value?.op || 'between'
  const v = value?.value
  const isColOp = op.endsWith('_col')

  return (
    <div className="flex items-center gap-2 border rounded px-2 py-1 bg-white">
      <span className="text-sm text-gray-700">{label}</span>
      <select className="h-8 px-2 text-sm border rounded" value={op} onChange={(e) => onChange({ op: e.target.value, value: null })}>
        {Operators.numerical.map((o) => (<option key={o.k} value={o.k}>{o.l}</option>))}
      </select>
      {op === 'between' ? (
        <>
          <input type="number" className="h-8 w-24 px-2 text-sm border rounded" placeholder="From" value={Array.isArray(v) ? (v?.[0] ?? '') : ''} onChange={(e) => onChange({ op, value: [e.target.value, Array.isArray(v) ? v?.[1] : ''] })} />
          <input type="number" className="h-8 w-24 px-2 text-sm border rounded" placeholder="To" value={Array.isArray(v) ? (v?.[1] ?? '') : ''} onChange={(e) => onChange({ op, value: [Array.isArray(v) ? v?.[0] : '', e.target.value] })} />
        </>
      ) : isColOp ? (
        <select
          className="h-8 px-2 text-sm border rounded bg-blue-50 border-blue-200"
          value={v ?? ''}
          onChange={(e) => onChange({ op, value: e.target.value })}
        >
          <option value="">Select Metric</option>
          {fields.filter(f => f.type === 'numerical' && f.key !== value.key).map(f => (
            <option key={f.key} value={f.key}>{f.label}</option>
          ))}
        </select>
      ) : (op !== 'blank' && op !== 'notBlank') ? (
        <input type="number" className="h-8 w-28 px-2 text-sm border rounded" placeholder="Value" value={v ?? ''} onChange={(e) => onChange({ op, value: e.target.value })} />
      ) : null}
      <button onClick={onRemove} className="text-gray-400 hover:text-gray-600" title="Remove">×</button>
    </div>
  )
}

const AddFilterMenu = ({ fields, onAdd }) => {
  const [open, setOpen] = useState(false)
  const btnRef = useRef(null)
  const menuRef = useRef(null)
  const [q, setQ] = useState('')
  const { coords, update } = usePortalPosition(btnRef, 320)
  const filtered = useMemo(() => fields.filter((f) => f.label.toLowerCase().includes(q.toLowerCase())), [fields, q])

  useLayoutEffect(() => { if (open) { update() } }, [open])
  useEffect(() => {
    const onDoc = (e) => {
      const insideMenu = menuRef.current && menuRef.current.contains(e.target)
      const insideBtn = btnRef.current && btnRef.current.contains(e.target)
      if (!insideMenu && !insideBtn) setOpen(false)
    }
    if (open) document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  return (
    <div className="relative inline-block">
      <button ref={btnRef} className="h-9 px-3 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm" onClick={() => setOpen((o) => !o)}>+ Add Filters</button>
      {open && createPortal(
        <div
          ref={menuRef}
          className="z-50 bg-white border border-gray-200 rounded-lg shadow-xl p-3"
          style={{ position: 'fixed', top: coords.top, left: coords.left, width: coords.width, maxHeight: '70vh', overflow: 'auto' }}
        >
          <input className="w-full h-9 px-3 text-sm border border-gray-300 rounded mb-2" placeholder={`Search ${fields.length} filters...`} value={q} onChange={(e) => setQ(e.target.value)} />
          <div className="overflow-auto">
            {filtered.map((f) => (
              <button key={f.key} onClick={() => { onAdd(f); setOpen(false) }} className="w-full text-left px-2 py-1 text-sm hover:bg-gray-50 rounded">
                {f.label}
              </button>
            ))}
          </div>
        </div>, document.body)}
    </div>
  )
}

export default function StockScreenerFilters({ fields, filters, onChange, onClear }) {
  const addFilter = (field) => {
    const exists = filters.find((f) => f.key === field.key)
    if (exists) return
    const def = field.type === 'categorical' ? { key: field.key, type: field.type, op: 'in', value: [] } : { key: field.key, type: field.type, op: 'between', value: ['', ''] }
    onChange([...filters, def])
  }
  const updateFilter = (idx, payload) => {
    const next = filters.slice()
    next[idx] = { ...next[idx], ...payload }
    onChange(next)
  }
  const removeFilter = (idx) => {
    const next = filters.slice()
    next.splice(idx, 1)
    onChange(next)
  }

  return (
    <div className="w-full border rounded-md bg-gray-50 p-3 mb-3">
      <div className="flex items-center gap-3 flex-wrap">
        <AddFilterMenu fields={fields} onAdd={addFilter} />
        {filters.map((f, idx) => (
          <FilterChip
            key={f.key}
            fields={fields}
            field={fields.find((x) => x.key === f.key)}
            value={f}
            onChange={(val) => updateFilter(idx, val)}
            onRemove={() => removeFilter(idx)}
          />
        ))}
        {filters.length > 0 && (
          <button onClick={onClear} className="ml-auto h-9 px-3 text-sm bg-gray-100 hover:bg-gray-200 rounded border">Remove Filters</button>
        )}
      </div>
    </div>
  )
}


